/**
 * ArtifactPreview — мини-визуализация артефакта для reveal-overlay и
 * minor toast. Раньше был один универсальный gold disc для всех — JB
 * 2026-05-26: «было бы круто если бы на превью как раз был круг
 * вращающийся по часовой… там где новая звезда добавилась тоже превью».
 *
 * Для известных id рендерится конкретный визуал (rotating ring, single
 * star pulse, fractal, constellation, double breath). Для остальных
 * fallback — generic warm disc.
 *
 * Размер контролируется через size prop (default 120 для major reveal,
 * 36 для minor toast).
 */
import styles from './ArtifactPreview.module.css';

interface ArtifactPreviewProps {
  readonly artifactId: string;
  /** Size в пикселях (квадрат). */
  readonly size?: number;
}

export function ArtifactPreview({ artifactId, size = 120 }: ArtifactPreviewProps): JSX.Element {
  const inner = renderInner(artifactId);
  return (
    <div
      className={styles.frame}
      style={{ width: `${size.toString()}px`, height: `${size.toString()}px` }}
      aria-hidden
    >
      {inner}
    </div>
  );
}

function renderInner(id: string): JSX.Element {
  switch (id) {
    case 'first_crystallization_ring':
      // Один тонкий gold ring вращается по часовой.
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={`${styles.ring ?? ''} ${styles.ringClockwise ?? ''}`} />
        </>
      );

    case 'tenth_crystallization_ring':
      // Два кольца — внутреннее по часовой, внешнее против.
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={`${styles.ring ?? ''} ${styles.ringClockwise ?? ''}`} />
          <div className={`${styles.ringOuter ?? ''} ${styles.ringCounter ?? ''}`} />
        </>
      );

    case 'fiftieth_crystallization_fractal':
      // Фрактальный узор внутри Искры — несколько вложенных кругов.
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={styles.fractalRing1} />
          <div className={styles.fractalRing2} />
          <div className={styles.fractalRing3} />
        </>
      );

    case 'hundred_taps_star':
      // Одна крупная звезда на тёмном фоне сети.
      return (
        <>
          <div className={styles.bgNetwork} />
          <div className={styles.brightStar} />
        </>
      );

    case 'thousand_taps_constellation':
      // 3 звезды + линии между ними.
      return (
        <>
          <div className={styles.bgNetwork} />
          <svg className={styles.constellation} viewBox="0 0 100 100">
            <line x1="20" y1="30" x2="55" y2="50" stroke="rgba(242,201,92,0.7)" strokeWidth="1" />
            <line x1="55" y1="50" x2="80" y2="25" stroke="rgba(242,201,92,0.7)" strokeWidth="1" />
            <circle cx="20" cy="30" r="2.5" fill="rgba(255,247,196,0.95)" />
            <circle cx="55" cy="50" r="2.8" fill="rgba(255,247,196,0.95)" />
            <circle cx="80" cy="25" r="2.5" fill="rgba(255,247,196,0.95)" />
          </svg>
        </>
      );

    case 'five_rhythm_pulse':
      // Искра с двойным дыханием — два слоя свечения в противофазе.
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={styles.breathOuter} />
          <div className={styles.breathInner} />
        </>
      );

    case 'name_signature':
      // Подпись — стилизованная закорючка снизу от Искры.
      return (
        <>
          <div className={styles.coreDisc} />
          <svg className={styles.signature} viewBox="0 0 100 100">
            <path
              d="M 25 75 Q 35 65, 45 75 T 65 75 Q 75 70, 80 78"
              stroke="rgba(242,201,92,0.85)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </>
      );

    case 'upgrade_silence_keeper_unlocked':
      // Хранитель тишины (b001) — холодная moss-зелёная точка справа.
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={styles.companionDot} />
        </>
      );

    case 'upgrade_silence_keeper_2_unlocked':
      // Хранитель тишины ур.2 (b002) — крупнее, ярче — тот же Проводник
      // но «увереннее». Читается как эволюция, не новый персонаж.
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={styles.companionDotLarge} />
        </>
      );

    case 'upgrade_alchemist_unlocked':
      // Алхимик (b003) — второй Проводник, СЛЕВА от Искры, тёплый
      // жёлто-зелёный. Цветом отличается от Хранителя — игрок видит
      // что это два разных присутствия рядом.
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={styles.companionDotAlchemist} />
        </>
      );

    case 'upgrade_gardener_unlocked':
      // Садовник — третий Проводник, землистый-зелёный снизу-справа.
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={styles.companionDotEarth} />
        </>
      );

    case 'upgrade_teacher_unlocked':
      // Учитель (b030) — четвёртый Проводник, охристо-золотой слева
      // от Искры. Тон эпохи 4: «через тебя идёт свет», метод как путь.
      // Сдвинут левее остальных Проводников чтобы создать ощущение
      // полуокружения вокруг Искры (5 Проводников: верх / правый /
      // нижний-правый / левый / нижний).
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={styles.companionDotTeacher} />
        </>
      );

    case 'upgrade_earth_keeper_unlocked':
      // Хранитель Земли (b040) — пятый Проводник, мшистый-зелёный
      // снизу, чуть крупнее остальных (это «земляная» точка опоры).
      // Тон эпохи 5: «место выбрало тебя».
      return (
        <>
          <div className={styles.coreDisc} />
          <div className={styles.companionDotEarthKeeper} />
        </>
      );

    default:
      // Generic warm disc для остальных артефактов (большинство
      // upgrade-артефактов = эффекты на самой Искре, превью просто яркий
      // gold orb).
      return <div className={styles.coreDisc} />;
  }
}
